import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DialogWrapper from 'components/DialogWrapper';
import ArgonBox from 'components/ArgonBox';
import ArgonButton from 'components/ArgonButton';
import Swal from 'sweetalert2';
import { SketchPicker } from 'react-color';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import ObjectID from 'bson-objectid';

function ItemDetailDialog({
  item,
  addNew,
  open,
  onClose,
  onChange,
  onRemove,
  dialogTitle,
  component,
  selectColor,
  size = 'sm'
}) {
  // Initialize with item if provided, otherwise empty object
  const [itemData, setItemData] = useState(item || {});

  // Erstelle ein formik-ähnliches Objekt für Komponenten, die formik erwarten
  // Verwende useMemo, um das formik-Objekt neu zu erstellen, wenn sich itemData ändert
  const formik = React.useMemo(
    () => ({
      values: itemData,
      initialValues: itemData,
      handleChange: (e) => {
        const { name, value } = e.target;
        setItemData((prev) => ({
          ...prev,
          [name]: value
        }));
      },
      handleBlur: () => {
        // Leere Implementierung, wird nur für die Kompatibilität benötigt
      },
      setValues: (newValues) => {
        console.log('ItemDetailDialog: setValues called with:', newValues);
        setItemData(newValues);
      },
      setFieldValue: (field, value) => {
        console.log('ItemDetailDialog: setFieldValue called:', field, value);
        setItemData((prev) => ({
          ...prev,
          [field]: value
        }));
      },
      validateForm: () => {
        // Leere Implementierung, wird nur für die Kompatibilität benötigt
        return Promise.resolve({});
      },
      handleSubmit: () => {
        onChange?.(itemData);
      },
      resetForm: () => {
        console.log('ItemDetailDialog: resetForm called with item:', item);
        setItemData(item || {});
      },
      touched: {},
      errors: {},
      isSubmitting: false,
      dirty: false
    }),
    [itemData, onChange, item]
  ); // Abhängigkeiten für useMemo

  // Aktualisiere den State, wenn sich das Item oder der open-Status ändert
  useEffect(() => {
    if (open) {
      if (addNew) {
        // For new items, start with empty state
        const emptyState = {
          _id: new ObjectID().toHexString(),
          exercise: '',
          progressions: [
            {
              _id: new ObjectID().toHexString(),
              progression: '',
              focus: '',
              duration: '',
              sets: '',
              reps: '',
              rest: ''
            }
          ]
        };
        setItemData(emptyState);
      } else if (item) {
        // For editing, use the provided item
        console.log('ItemDetailDialog: updating item:', item);
        // Wichtig: Wir müssen eine Kopie des Objekts erstellen, um sicherzustellen,
        // dass React die Änderung erkennt und die Komponente neu rendert
        setItemData({ ...item });
      }
    }
  }, [item, open, addNew]);

  const handleCancelButtonClicked = (e) => {
    e.preventDefault();
    // Reset to original item data on cancel
    setItemData(item || {});
    onClose?.();
  };

  const handleRemoveButtonClicked = (e) => {
    e.preventDefault();

    Swal.fire({
      title: 'Are you sure?',
      text: 'This item will be removed from the list. However, you will be able to add it again anytime.',
      icon: 'warning',
      showCancelButton: true
    }).then((result) => {
      if (result.isConfirmed) {
        onRemove?.(itemData);
        onClose?.();
      }
    });
  };

  const handleCloneButtonClicked = (e) => {
    e.preventDefault();

    // Generate a new ObjectID for the cloned item
    const newId = new ObjectID().toHexString();

    // Create a clone of the current item with a new ID
    const clonedItem = {
      ...itemData,
      _id: newId // Set a new ObjectID
      // id: newId // Also set the id field to match
    };

    // Wenn ein Label oder Name vorhanden ist, füge "(Copy)" hinzu
    if (clonedItem.label) {
      clonedItem.label = `${clonedItem.label} (Copy)`;
    } else if (clonedItem.name) {
      clonedItem.name = `${clonedItem.name} (Copy)`;
    } else if (clonedItem.excercise) {
      clonedItem.excercise = `${clonedItem.excercise} (Copy)`;
    }

    // Log for debugging
    console.log('Cloning item:', clonedItem);

    // Submit the cloned item
    onChange?.(clonedItem);
    onClose?.();
  };

  const handleColorChange = (color) => {
    setItemData((prev) => ({ ...prev, color: color?.hex }));
  };

  const handleSubmit = () => {
    onChange?.(itemData);
  };

  const renderCustomForm = () => {
    if (component) {
      if (React.isValidElement(component)) {
        return React.cloneElement(component, {
          formik, // Übergebe das formik-ähnliche Objekt
          item: itemData,
          onChange: (values) => {
            // Direkt den State aktualisieren, ohne Transformation
            if (values && JSON.stringify(itemData) !== JSON.stringify(values)) {
              setItemData(values);
            }
          }
        });
      } else {
        // If it's a component reference, create an element from it
        const Component = component;
        return (
          <Component
            formik={formik} // Übergebe das formik-ähnliche Objekt
            item={itemData}
            onChange={(values) => {
              // Direkt den State aktualisieren, ohne Transformation
              if (
                values &&
                JSON.stringify(itemData) !== JSON.stringify(values)
              ) {
                setItemData(values);
              }
            }}
          />
        );
      }
    }

    // Fallback-Formular, wenn keine Komponente übergeben wurde
    return (
      <ArgonBox>
        <ArgonBox mb={2}>
          <label htmlFor="label">Label</label>
          <input
            id="label"
            name="label"
            type="text"
            onChange={(e) =>
              setItemData((prev) => ({ ...prev, label: e.target.value }))
            }
            value={itemData.label || ''}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid #ccc'
            }}
          />
        </ArgonBox>
        {selectColor && (
          <ArgonBox mb={2}>
            <label>Color</label>
            <SketchPicker
              color={itemData.color}
              onChange={handleColorChange}
              disableAlpha
            />
          </ArgonBox>
        )}
      </ArgonBox>
    );
  };

  return (
    <DialogWrapper
      title={dialogTitle || 'Item details'}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={size}>
      {renderCustomForm()}

      <ArgonBox mt={4} display="flex" justifyContent="space-between" gap={2}>
        <ArgonBox display="flex" gap={2}>
          <ArgonButton
            variant="outlined"
            color="secondary"
            size="small"
            onClick={handleCancelButtonClicked}>
            Cancel
          </ArgonButton>
        </ArgonBox>
        <ArgonBox display="flex" gap={2}>
          {!addNew && (
            <>
              <ArgonButton
                variant="outlined"
                color="info"
                size="small"
                startIcon={<ContentCopyIcon />}
                onClick={handleCloneButtonClicked}>
                Clone
              </ArgonButton>
              <ArgonButton
                variant="outlined"
                color="error"
                size="small"
                startIcon={<DeleteIcon />}
                onClick={handleRemoveButtonClicked}>
                Delete
              </ArgonButton>
            </>
          )}
          <ArgonButton
            variant="gradient"
            color="info"
            size="small"
            onClick={handleSubmit}>
            {addNew ? 'Add' : 'Save'}
          </ArgonButton>
        </ArgonBox>
      </ArgonBox>
    </DialogWrapper>
  );
}

ItemDetailDialog.propTypes = {
  item: PropTypes.object, // Allgemeines Objekt ohne spezifische Struktur
  addNew: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onRemove: PropTypes.func,
  dialogTitle: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
  selectColor: PropTypes.bool,
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl'])
};

export default ItemDetailDialog;
